/**
 * AI CONTEXT: admin-feature - Animated winner picker modal for social media recording
 * Journal: J-20250120-01
 * 
 * Purpose:
 * - Display giveaway slot grid with countdown timer
 * - Animate slot selection with glass morphic effects
 * - Randomly flash slots red/gray for 10 seconds
 * - Reveal winner with flip animation
 * - Designed for social media recording
 * 
 * Public API:
 * <WinnerPickerModal
 *   giveaway={giveaway}
 *   entries={entries}
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   onWinnerSelected={onWinnerSelected}
 * />
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Eye, EyeOff, Gift } from 'lucide-react';
import { authorizedFetch } from '../services/apiClient';
import toast from 'react-hot-toast';
import S3Image from './S3Image';

const WinnerPickerModal = ({ 
  giveaway, 
  entries = [], 
  isOpen, 
  onClose, 
  onWinnerSelected 
}) => {
  const [countdown, setCountdown] = useState(10);
  const [isAnimating, setIsAnimating] = useState(false);
  const [winningSlot, setWinningSlot] = useState(null);
  const [winnerInfo, setWinnerInfo] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [slotStates, setSlotStates] = useState({}); // Track slot colors: 'gray', 'red', 'normal'
  const animationIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const allSlotsRef = useRef([]);
  const reservedSlotsRef = useRef(new Set());

  // Initialize slot data
  useEffect(() => {
    if (!giveaway || !isOpen) return;
    
    const totalSlots = giveaway.totalSlots || 0;
    allSlotsRef.current = Array.from({ length: totalSlots }, (_, i) => i + 1);
    
    // Get reserved slots from entries
    const reserved = new Set();
    entries.forEach(entry => {
      if (entry.slotNumber) {
        reserved.add(parseInt(entry.slotNumber));
      }
    });
    reservedSlotsRef.current = reserved;
    
    // Initialize all slots to normal state
    const initialStates = {};
    allSlotsRef.current.forEach(slot => {
      initialStates[slot] = 'normal';
    });
    setSlotStates(initialStates);
  }, [giveaway, entries, isOpen]);

  // Cleanup on unmount or close
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setCountdown(10);
      setIsAnimating(false);
      setWinningSlot(null);
      setWinnerInfo(null);
      setIsRevealed(false);
      setShowEmail(false);
      setSlotStates({});
      
      // Clear intervals
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [isOpen]);

  const stopScrollPropagation = (e) => {
    e.stopPropagation();
  };

  const selectWinnerFromAPI = useCallback(async () => {
    try {
      const requestBody = { giveawayId: giveaway.id };
      
      // If a winner already exists, send override flag
      // (User already confirmed in handlePickRandomWinner before animation started)
      // Check for winner in various possible formats
      const hasWinner = giveaway.winner || giveaway.winnerEmail || giveaway.winnerSlot;
      if (hasWinner) {
        requestBody.override = true;
        console.log('🔍 Winner exists, sending override flag');
      } else {
        console.log('🔍 No existing winner found');
      }

      console.log('🔍 Request body:', requestBody);

      const response = await authorizedFetch('/admin/select-automatic-winner', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (response && response.winner) {
        const winner = response.winner;
        const winnerSlot = parseInt(winner.slot);
        
        // Set winning slot to red and keep it red
        setWinningSlot(winnerSlot);
        setSlotStates(prev => ({
          ...prev,
          [winnerSlot]: 'red'
        }));

        // Get winner info from entries
        const winnerEntry = entries.find(e => parseInt(e.slotNumber) === winnerSlot);
        if (winnerEntry) {
          const fullName = winnerEntry.name?.split(' ') || [];
          const firstName = fullName[0] || '';
          const lastName = fullName.slice(1).join(' ') || '';
          setWinnerInfo({
            firstName: firstName,
            lastName: lastName,
            lastInitial: lastName ? lastName[0] : '',
            email: winnerEntry.email || winner.email
          });
        } else {
          // Fallback to email if name not found
          const emailParts = winner.email.split('@')[0].split('.');
          const firstName = emailParts[0] || '';
          const lastName = emailParts[1] || '';
          setWinnerInfo({
            firstName: firstName,
            lastName: lastName,
            lastInitial: lastName ? lastName[0] : '',
            email: winner.email
          });
        }

        // Trigger flip animation after a brief delay
        setTimeout(() => {
          setIsRevealed(true);
          setIsAnimating(false);
          
          // Notify parent
          if (onWinnerSelected) {
            onWinnerSelected(winner);
          }
        }, 500);
      } else {
        throw new Error('Failed to select winner');
      }
    } catch (error) {
      console.error('Error selecting winner:', error);
      
      // Stop all animations
      setIsAnimating(false);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      
      // Reset countdown
      setCountdown(10);
      
      // Reset slot states to normal
      const normalStates = {};
      allSlotsRef.current.forEach(slot => {
        normalStates[slot] = 'normal';
      });
      setSlotStates(normalStates);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to select winner';
      if (errorMessage.includes('No valid entries')) {
        toast.error('No valid entries found for this giveaway. All entries may be from admin accounts.');
      } else if (errorMessage.includes('Winner already exists')) {
        toast.error('A winner already exists for this giveaway. The override parameter should allow re-selection, but the backend may need to be updated.');
      } else {
        toast.error(errorMessage);
      }
    }
  }, [giveaway, entries, onWinnerSelected]);

  const handlePickRandomWinner = useCallback(async () => {
    if (!giveaway || isAnimating) return;

    // If a winner already exists, prompt to override
    // Check for winner in various possible formats
    const hasWinner = giveaway.winner || giveaway.winnerEmail || giveaway.winnerSlot;
    if (hasWinner) {
      const confirmed = window.confirm(
        "A winner already exists for this giveaway. Do you want to select a new winner and override the existing one?"
      );
      if (!confirmed) {
        return; // User cancelled
      }
    }

    setIsAnimating(true);
    setCountdown(10);
    setWinningSlot(null);
    setWinnerInfo(null);
    setIsRevealed(false);

    // Set all slots to glass morphic dark gray
    const grayStates = {};
    allSlotsRef.current.forEach(slot => {
      grayStates[slot] = 'gray';
    });
    setSlotStates(grayStates);

    // Start countdown
    let currentCountdown = 10;
    countdownIntervalRef.current = setInterval(() => {
      currentCountdown -= 1;
      setCountdown(currentCountdown);
      
      if (currentCountdown <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }, 1000);

    // Animation: randomly flash slots red then gray
    let animationStartTime = Date.now();
    const animationDuration = 10000; // 10 seconds
    const flashInterval = 150; // Flash every 150ms for smooth animation

    animationIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - animationStartTime;
      
      if (elapsed >= animationDuration) {
        // Animation complete - stop flashing and select winner
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
        
        // Call API to select winner
        selectWinnerFromAPI();
      } else {
        // Randomly select a slot to flash red (only reserved slots)
        const availableSlots = allSlotsRef.current.filter(
          slot => reservedSlotsRef.current.has(slot)
        );
        
        if (availableSlots.length > 0) {
          const randomSlot = availableSlots[Math.floor(Math.random() * availableSlots.length)];
          
          // Flash red
          setSlotStates(prev => ({
            ...prev,
            [randomSlot]: 'red'
          }));
          
          // Then turn back to gray after a short delay
          setTimeout(() => {
            setSlotStates(prev => ({
              ...prev,
              [randomSlot]: 'gray'
            }));
          }, 100);
        }
      }
    }, flashInterval);
  }, [giveaway, isAnimating, selectWinnerFromAPI]);

  if (!isOpen || !giveaway) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4"
      onClick={onClose}
      style={{
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(30, 30, 30, 0.85) 100%)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Animated background particles */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(circle at 20% 50%, rgba(245, 158, 11, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(251, 191, 36, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, rgba(217, 119, 6, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 60% 60%, rgba(234, 179, 8, 0.1) 0%, transparent 50%)
          `,
          animation: 'backgroundPulse 8s ease-in-out infinite',
          zIndex: 0
        }}
      />
      
      {/* Animated gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(45deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 25%, rgba(217, 119, 6, 0.1) 50%, rgba(234, 179, 8, 0.1) 75%, rgba(245, 158, 11, 0.1) 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 10s ease infinite',
          zIndex: 0
        }}
      />
      
      <div
        className="glass-card-dark rounded-xl max-w-5xl lg:max-w-7xl w-full max-h-[96vh] sm:max-h-[98vh] overflow-hidden relative"
        style={{ 
          WebkitOverflowScrolling: 'touch', 
          overscrollBehavior: 'contain', 
          touchAction: 'none',
          background: 'rgba(20, 20, 30, 0.4)',
          backdropFilter: 'blur(30px) saturate(180%)',
          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          boxShadow: `
            0 8px 32px 0 rgba(0, 0, 0, 0.37),
            0 0 50px rgba(245, 158, 11, 0.2),
            0 0 100px rgba(251, 191, 36, 0.15),
            inset 0 0 30px rgba(245, 158, 11, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `,
          position: 'relative',
          zIndex: 10000
        }}
        onWheel={stopScrollPropagation}
        onTouchMove={stopScrollPropagation}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glass morphism overlay for extra depth */}
        <div 
          className="absolute inset-0 rounded-xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 'inherit'
          }}
        />
        
        {/* Animated corner accents */}
        <div className="absolute top-0 left-0 w-32 h-32 pointer-events-none" style={{ opacity: 0.5 }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.3) 0%, transparent 70%)',
            borderRadius: '12px 0 0 0',
            animation: 'pulse 4s ease-in-out infinite'
          }} />
        </div>
        <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none" style={{ opacity: 0.5 }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(225deg, rgba(251, 191, 36, 0.3) 0%, transparent 70%)',
            borderRadius: '0 12px 0 0',
            animation: 'pulse 4s ease-in-out infinite',
            animationDelay: '2s'
          }} />
        </div>
        <div className="absolute bottom-0 left-0 w-32 h-32 pointer-events-none" style={{ opacity: 0.5 }}>
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(45deg, rgba(217, 119, 6, 0.3) 0%, transparent 70%)',
            borderRadius: '0 0 0 12px',
            animation: 'pulse 4s ease-in-out infinite',
            animationDelay: '1s'
          }} />
        </div>
        <div className="absolute bottom-0 right-0 w-32 h-32 pointer-events-none" style={{ opacity: 0.5 }}>
          <div style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(315deg, rgba(245, 158, 11, 0.3) 0%, transparent 70%)',
            borderRadius: '0 0 12px 0',
            animation: 'pulse 4s ease-in-out infinite',
            animationDelay: '3s'
          }} />
        </div>
        {/* Close button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 z-50 text-theme-silver hover:text-white transition-colors bg-black/20 rounded-full p-2 backdrop-blur-sm"
          aria-label="Close modal"
          style={{ pointerEvents: 'auto' }}
        >
          <X className="h-6 w-6" />
        </button>

        <div className="p-3 sm:p-4 md:p-6 max-h-[92vh] sm:max-h-[93vh] overflow-y-auto relative z-20" style={{ fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
          {/* Decorative top border accent */}
          <div 
            className="absolute top-0 left-0 right-0 h-1"
            style={{
              background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.8) 0%, rgba(251, 191, 36, 0.8) 100%)',
              boxShadow: '0 0 15px rgba(245, 158, 11, 0.4)'
            }}
          />
          
          {/* Header */}
          <div className="text-center mb-3 sm:mb-4 md:mb-6 relative">
            <div className="mb-4">
              <p 
                className="text-base sm:text-lg md:text-xl lg:text-2xl font-extrabold"
                style={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '0 0 15px rgba(251, 191, 36, 0.4)',
                  animation: 'pulse 2s ease-in-out infinite',
                  letterSpacing: '0.03em',
                  lineHeight: '1.3',
                  fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
                }}
              >
                🎉 Enter next giveaway at TolbertTactical.com 🎉
              </p>
            </div>
            <h2 
              className="text-lg sm:text-xl md:text-2xl font-extrabold mb-2 sm:mb-3 md:mb-4"
              style={{
                color: '#9CA3AF',
                textShadow: `
                  0 0 10px rgba(156, 163, 175, 0.8),
                  0 0 20px rgba(209, 213, 219, 0.6),
                  0 0 30px rgba(156, 163, 175, 0.4),
                  0 2px 4px rgba(0, 0, 0, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.4)
                `,
                letterSpacing: '0.02em',
                lineHeight: '1.2',
                animation: 'metallicShimmer 2s ease-in-out infinite',
                filter: 'brightness(1.1)',
                fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                fontWeight: 800
              }}
            >
              Giveaway: {giveaway.title}
            </h2>
          </div>

          {/* Countdown Timer with Giveaway Image - Hide when winner is revealed */}
          {!isRevealed && (
            <div className="mb-3 sm:mb-4 md:mb-6 flex justify-center">
              <div className="flex flex-row items-center gap-2 sm:gap-3 md:gap-4">
                {/* Giveaway Image */}
                <div 
                  className="glass-card rounded-xl border-4 relative overflow-hidden flex-shrink-0 w-[125px] h-[125px] sm:w-[140px] sm:h-[140px] md:w-[160px] md:h-[160px] lg:w-[200px] lg:h-[200px]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.2) 100%)',
                    borderColor: 'rgba(245, 158, 11, 0.6)',
                    boxShadow: '0 0 30px rgba(245, 158, 11, 0.5), inset 0 0 20px rgba(245, 158, 11, 0.2)'
                  }}
                >
                  <S3Image
                    s3KeyOrUrl={giveaway.image || '/files/Logo.png'}
                    alt={giveaway.title || 'Giveaway'}
                    className="w-full h-full object-cover rounded-lg"
                    fallback="/files/Logo.png"
                  />
                </div>

                {/* Countdown Timer */}
                <div 
                  className="glass-card p-2 sm:p-3 md:p-4 lg:p-6 rounded-xl border-4 relative overflow-hidden flex flex-col justify-center items-center w-[125px] h-[125px] sm:w-[140px] sm:h-[140px] md:w-[160px] md:h-[160px] lg:w-[200px] lg:h-[200px]"
                  style={{
                    background: countdown > 0 
                      ? `linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(168, 85, 247, 0.2) 50%, rgba(236, 72, 153, 0.2) 100%)`
                      : `linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)`,
                    borderColor: countdown > 0 
                      ? 'rgba(239, 68, 68, 0.6)' 
                      : 'rgba(34, 197, 94, 0.8)',
                    boxShadow: countdown > 0
                      ? `0 0 30px rgba(239, 68, 68, 0.5), 0 0 60px rgba(168, 85, 247, 0.3), inset 0 0 20px rgba(236, 72, 153, 0.2)`
                      : `0 0 40px rgba(34, 197, 94, 0.6), 0 0 80px rgba(59, 130, 246, 0.4)`,
                    animation: countdown > 0 ? 'pulse 1s ease-in-out infinite' : 'none'
                  }}
                >
                  {/* Animated background glow */}
                  {countdown > 0 && (
                    <div 
                      className="absolute inset-0 rounded-xl"
                      style={{
                        background: `radial-gradient(circle at center, rgba(239, 68, 68, 0.3) 0%, transparent 70%)`,
                        animation: 'pulse 2s ease-in-out infinite',
                        zIndex: 0
                      }}
                    />
                  )}
                  
                  <div className="text-center relative z-10 w-full">
                    <div 
                      className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-0.5 sm:mb-1 md:mb-2 transition-all duration-300"
                      style={{
                        color: countdown > 0 
                          ? `hsl(${(countdown * 36) % 360}, 90%, 60%)` // Rotating hue based on countdown
                          : '#22c55e', // Green when done
                        textShadow: countdown > 0
                          ? `0 0 20px rgba(239, 68, 68, 0.8), 0 0 40px rgba(168, 85, 247, 0.6), 0 0 60px rgba(236, 72, 153, 0.4)`
                          : `0 0 30px rgba(34, 197, 94, 0.8), 0 0 60px rgba(59, 130, 246, 0.6)`,
                        transform: countdown > 0 ? `scale(${1 + (10 - countdown) * 0.05})` : 'scale(1.1)',
                        animation: countdown > 0 ? 'bounce 0.5s ease-in-out' : 'none',
                        fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                        fontWeight: 900,
                        letterSpacing: '-0.02em',
                        lineHeight: '1'
                      }}
                    >
                      {countdown}
                    </div>
                    <div 
                      className="text-[10px] sm:text-xs md:text-sm lg:text-base font-bold uppercase tracking-widest transition-colors duration-300"
                      style={{
                        color: countdown > 0 
                          ? '#fbbf24' // Gold/amber
                          : '#22c55e', // Green
                        textShadow: countdown > 0
                          ? '0 0 10px rgba(251, 191, 36, 0.8)'
                          : '0 0 10px rgba(34, 197, 94, 0.8)',
                        fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        lineHeight: '1.4'
                      }}
                    >
                      {countdown > 0 ? 'Seconds Remaining' : '🎉 Winner Selected! 🎉'}
                    </div>
                    {countdown > 0 && (
                      <div className="mt-0.5 sm:mt-1 md:mt-2 text-[8px] sm:text-[10px] md:text-xs text-theme-silver-light font-medium flex items-center justify-center gap-0.5 sm:gap-1 md:gap-2" style={{ 
                        fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                        letterSpacing: '0.05em'
                      }}>
                        <span className="hidden sm:inline">Get ready for the reveal!</span>
                        <Gift className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4" style={{ color: '#fbbf24' }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Add CSS animations */}
          <style>{`
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.8;
                transform: scale(1.02);
              }
            }
            @keyframes bounce {
              0%, 100% {
                transform: scale(1);
              }
              50% {
                transform: scale(1.1);
              }
            }
            @keyframes backgroundPulse {
              0%, 100% {
                opacity: 0.6;
                transform: scale(1);
              }
              50% {
                opacity: 1;
                transform: scale(1.1);
              }
            }
            @keyframes gradientShift {
              0% {
                background-position: 0% 50%;
              }
              50% {
                background-position: 100% 50%;
              }
              100% {
                background-position: 0% 50%;
              }
            }
            @keyframes shimmer {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(200%);
              }
            }
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
            @keyframes metallicShimmer {
              0%, 100% {
                textShadow: 
                  0 0 10px rgba(156, 163, 175, 0.8),
                  0 0 20px rgba(209, 213, 219, 0.6),
                  0 0 30px rgba(156, 163, 175, 0.4),
                  0 2px 4px rgba(0, 0, 0, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.4);
                filter: 'brightness(1.1)';
              }
              50% {
                textShadow: 
                  0 0 15px rgba(209, 213, 219, 0.9),
                  0 0 30px rgba(156, 163, 175, 0.7),
                  0 0 45px rgba(229, 231, 235, 0.5),
                  0 2px 4px rgba(0, 0, 0, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.6);
                filter: 'brightness(1.2)';
              }
            }
          `}</style>

          {/* Slot Grid with Animation */}
          <div className="mb-3 sm:mb-4 md:mb-6 relative">
            {/* Grid container with enhanced styling */}
            <div 
              className="grid grid-cols-5 gap-1 sm:gap-2 max-h-[260px] sm:max-h-[250px] md:max-h-[300px] lg:max-h-[400px] overflow-y-auto p-2 sm:p-4 rounded-lg relative"
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: 'inset 0 0 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(245, 158, 11, 0.2)'
              }}
            >
              {allSlotsRef.current.map(slot => {
                const isReserved = reservedSlotsRef.current.has(slot);
                const slotState = slotStates[slot] || 'normal';
                const isWinning = winningSlot === slot;
                const isFlipped = isWinning && isRevealed;

                // Determine slot styling based on state
                let slotClasses = 'p-1 sm:p-2 rounded border text-xs sm:text-sm font-semibold transition-all duration-300 min-h-[35px] sm:min-h-[45px] md:min-h-[50px] flex items-center justify-center text-center';
                let slotContent = null;

                if (isFlipped) {
                  // Flipped card showing winner info - glassmorphic red
                  slotClasses += ' backdrop-blur-sm';
                  const redGlassStyle = {
                    background: 'rgba(239, 68, 68, 0.3)',
                    backdropFilter: 'blur(15px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(15px) saturate(180%)',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    color: '#ffffff',
                    fontWeight: 600,
                    boxShadow: 'inset 0 0 20px rgba(239, 68, 68, 0.4), 0 0 15px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                  };
                  slotContent = (
                    <div className="transform transition-all duration-500" style={{ transform: 'rotateY(180deg)' }}>
                      <div className="font-bold text-lg mb-1" style={{ 
                        fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                        letterSpacing: '0.02em'
                      }}>
                        {winnerInfo?.firstName || ''}
                      </div>
                      <div className="font-bold text-lg" style={{ 
                        fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                        letterSpacing: '0.02em'
                      }}>
                        {winnerInfo?.lastInitial || ''}
                      </div>
                    </div>
                  );
                  return (
                    <div
                      key={slot}
                      className={slotClasses}
                      style={{
                        ...redGlassStyle,
                        transform: 'rotateY(180deg) scale(1.1)',
                        transition: 'transform 0.6s ease-in-out, background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      {slotContent}
                    </div>
                  );
                } else if (isWinning) {
                  // Winning slot (glassmorphic red, not yet flipped) - stays red before flip
                  slotClasses += ' backdrop-blur-sm';
                  const winningRedGlassStyle = {
                    background: 'rgba(239, 68, 68, 0.3)',
                    backdropFilter: 'blur(15px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(15px) saturate(180%)',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    color: '#ffffff',
                    fontWeight: 600,
                    boxShadow: 'inset 0 0 20px rgba(239, 68, 68, 0.4), 0 0 15px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                    transform: 'scale(1.1)'
                  };
                  return (
                    <div
                      key={slot}
                      className={slotClasses}
                      style={{
                        ...winningRedGlassStyle,
                        transition: 'transform 0.6s ease-in-out, background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      {slot}
                    </div>
                  );
                } else if (slotState === 'red') {
                  // Flashing red during animation
                  slotClasses += ' bg-red-600 border-red-500 animate-pulse';
                  slotContent = isReserved ? (
                    <span style={{
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '1.1em'
                    }}>
                      R
                    </span>
                  ) : (
                    <span style={{ color: '#ffffff' }}>{slot}</span>
                  );
                } else if (slotState === 'gray') {
                  // Glass morphic gold
                  slotClasses += ' backdrop-blur-sm';
                  slotContent = isReserved ? (
                    <span style={{
                      color: '#ef4444',
                      fontWeight: 700,
                      fontSize: '1.1em'
                    }}>
                      R
                    </span>
                  ) : slot;
                  // Apply gold glassmorphic styling via inline styles
                  const goldGlassStyle = {
                    background: 'rgba(245, 158, 11, 0.3)',
                    backdropFilter: 'blur(15px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(15px) saturate(180%)',
                    border: '1px solid rgba(251, 191, 36, 0.5)',
                    color: isReserved ? 'transparent' : '#fbbf24',
                    fontWeight: 600,
                    boxShadow: 'inset 0 0 20px rgba(245, 158, 11, 0.4), 0 0 15px rgba(251, 191, 36, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                  };
                  return (
                    <div
                      key={slot}
                      className={slotClasses}
                      style={{
                        ...goldGlassStyle,
                        transform: isFlipped ? 'rotateY(180deg) scale(1.1)' : (isWinning ? 'scale(1.1)' : 'scale(1)'),
                        transition: 'transform 0.6s ease-in-out, background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      {slotContent}
                    </div>
                  );
                } else {
                  // Normal state - show all slots (reserved and unreserved) with gold glassmorphic styling
                  if (isReserved) {
                    slotClasses += ' backdrop-blur-sm';
                    slotContent = (
                      <span style={{
                        color: '#ef4444',
                        fontWeight: 700,
                        fontSize: '1.1em'
                      }}>
                        R
                      </span>
                    );
                    // Apply gold glassmorphic styling for reserved slots
                    const reservedGoldStyle = {
                      background: 'rgba(245, 158, 11, 0.2)',
                      backdropFilter: 'blur(12px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                      border: '1px solid rgba(251, 191, 36, 0.4)',
                      fontWeight: 600,
                      boxShadow: 'inset 0 0 15px rgba(245, 158, 11, 0.3), 0 0 10px rgba(251, 191, 36, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    };
                    return (
                      <div
                        key={slot}
                        className={slotClasses}
                        style={{
                          ...reservedGoldStyle,
                          transform: isFlipped ? 'rotateY(180deg) scale(1.1)' : (isWinning ? 'scale(1.1)' : 'scale(1)'),
                          transition: 'transform 0.6s ease-in-out, background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
                          transformStyle: 'preserve-3d'
                        }}
                      >
                        {slotContent}
                      </div>
                    );
                  } else {
                    slotClasses += ' backdrop-blur-sm';
                    slotContent = slot;
                    // Apply gold glassmorphic styling for available slots
                    const availableGoldStyle = {
                      background: 'rgba(245, 158, 11, 0.15)',
                      backdropFilter: 'blur(12px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                      border: '1px solid rgba(251, 191, 36, 0.3)',
                      color: '#fbbf24',
                      fontWeight: 600,
                      boxShadow: 'inset 0 0 15px rgba(245, 158, 11, 0.2), 0 0 8px rgba(251, 191, 36, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    };
                    return (
                      <div
                        key={slot}
                        className={slotClasses}
                        style={{
                          ...availableGoldStyle,
                          transform: isFlipped ? 'rotateY(180deg) scale(1.1)' : (isWinning ? 'scale(1.1)' : 'scale(1)'),
                          transition: 'transform 0.6s ease-in-out, background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
                          transformStyle: 'preserve-3d'
                        }}
                      >
                        {slotContent}
                      </div>
                    );
                  }
                }

                // Default return for isFlipped, isWinning, and red states
                return (
                  <div
                    key={slot}
                    className={slotClasses}
                    style={{
                      transform: isFlipped ? 'rotateY(180deg) scale(1.1)' : (isWinning ? 'scale(1.1)' : 'scale(1)'),
                      transition: 'transform 0.6s ease-in-out, background-color 0.3s ease-in-out',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    {slotContent}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pick Random Winner Button */}
          <div className="flex justify-center">
            <button
              onClick={handlePickRandomWinner}
              disabled={isAnimating}
              className="relative overflow-hidden group px-4 py-2 sm:px-6 sm:py-3 md:px-12 md:py-4 rounded-lg sm:rounded-xl"
              style={{
                backgroundColor: isAnimating 
                  ? 'rgba(245, 158, 11, 0.3)'
                  : 'rgba(245, 158, 11, 0.25)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                backgroundImage: isAnimating
                  ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.4) 0%, rgba(251, 191, 36, 0.4) 100%)'
                  : 'linear-gradient(135deg, rgba(245, 158, 11, 0.35) 0%, rgba(251, 191, 36, 0.35) 50%, rgba(217, 119, 6, 0.35) 100%)',
                backgroundSize: '200% 200%',
                animation: isAnimating ? 'gradientShift 2s ease infinite' : 'gradientShift 4s ease infinite',
                border: '2px solid',
                borderColor: isAnimating ? 'rgba(245, 158, 11, 0.5)' : 'rgba(251, 191, 36, 0.4)',
                boxShadow: isAnimating
                  ? '0 8px 32px 0 rgba(0, 0, 0, 0.37), 0 0 30px rgba(245, 158, 11, 0.6), 0 0 60px rgba(251, 191, 36, 0.4), inset 0 0 20px rgba(245, 158, 11, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  : '0 8px 32px 0 rgba(0, 0, 0, 0.37), 0 0 20px rgba(245, 158, 11, 0.5), 0 0 40px rgba(251, 191, 36, 0.3), inset 0 0 15px rgba(217, 119, 6, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                transform: 'scale(1)',
                transition: 'all 0.3s ease',
                cursor: isAnimating ? 'not-allowed' : 'pointer',
                opacity: isAnimating ? 0.8 : 1
              }}
              onMouseEnter={(e) => {
                if (!isAnimating) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(0, 0, 0, 0.37), 0 0 40px rgba(245, 158, 11, 0.7), 0 0 80px rgba(251, 191, 36, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isAnimating) {
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {/* Shimmer effect */}
              <div 
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                  transform: 'translateX(-100%)',
                  animation: 'shimmer 3s infinite',
                  pointerEvents: 'none'
                }}
              />
              
              {/* Button content */}
              <span 
                className="relative z-10 flex items-center gap-2 sm:gap-3 text-white font-black text-sm sm:text-base md:text-xl tracking-wide"
                style={{
                  textShadow: '0 0 10px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3)',
                  letterSpacing: '0.06em',
                  fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                  fontWeight: 800,
                  lineHeight: '1.3'
                }}
              >
                {isAnimating ? (
                  <>
                    <span className="inline-block animate-pulse">Picking Winner</span>
                    <span className="inline-flex gap-1">
                      <span className="animate-bounce" style={{ animationDelay: '0s' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '0.4s' }}>.</span>
                    </span>
                  </>
                ) : (
                  <>
                    🔫 Pick Random Winner 🔫
                  </>
                )}
              </span>
            </button>
          </div>

          {/* Winner Display Box - Below the button */}
          {isRevealed && winnerInfo && (
            <div className="mt-6 flex justify-center">
              <div 
                className="glass-card p-6 rounded-xl relative overflow-hidden w-full max-w-md"
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  border: '2px solid rgba(239, 68, 68, 0.4)',
                  boxShadow: '0 0 30px rgba(239, 68, 68, 0.3), inset 0 0 20px rgba(239, 68, 68, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                }}
              >
                <h3 
                  className="text-2xl font-bold mb-4 text-center"
                  style={{
                    color: '#ffffff',
                    textShadow: '0 0 10px rgba(239, 68, 68, 0.8)',
                    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    lineHeight: '1.2'
                  }}
                >
                  Winner Selected
                </h3>
                <div className="text-center mb-4">
                  <p className="text-2xl font-bold text-white mb-2" style={{ 
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
                    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    lineHeight: '1.3'
                  }}>
                    {winnerInfo.firstName} {winnerInfo.lastInitial}.
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-lg text-orange-200 font-medium" style={{
                      fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                      letterSpacing: '0.03em'
                    }}>
                      {showEmail ? winnerInfo.email : '****'}
                    </p>
                    <button
                      onClick={() => setShowEmail(!showEmail)}
                      className="p-1 hover:bg-white/10 rounded transition-colors"
                      aria-label={showEmail ? 'Hide email' : 'Show email'}
                      style={{
                        color: '#fbbf24',
                        cursor: 'pointer'
                      }}
                    >
                      {showEmail ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WinnerPickerModal;

